import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { PageSkeleton } from 'markcat-ui';
import { useAuthStore } from '../stores/auth';
import { rehydrateUser, restoreSession } from '../stores/session';
import styles from './SessionBootstrap.module.css';

type Phase = 'restoring' | 'done' | 'transient';

/**
 * Gate guarded routes behind a one-shot silent session hydration, so children
 * always render with the invariant "authenticated ⇒ user populated":
 *
 * - Access token persisted but `user` null (any normal reload — only login and
 *   restoreSession set `user`): rehydrate from /me; a stale token falls back
 *   to the cookie-refresh restore below.
 * - No access token (evicted localStorage) but a valid refresh cookie:
 *   rebuild auth via refresh → /me before the AuthGuard child decides, so the
 *   user isn't bounced to login despite a valid session.
 *
 * Shows login only on a terminal 401 (store cleared, AuthGuard redirects);
 * a transient failure shows a retry screen, never login.
 */
export function SessionBootstrap({ children }: { children: ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const hydrated = !!accessToken && !!user;
  const [phase, setPhase] = useState<Phase>(hydrated ? 'done' : 'restoring');
  const triedRef = useRef(false);

  const run = useCallback(() => {
    setPhase('restoring');
    void (async () => {
      if (useAuthStore.getState().accessToken) {
        const outcome = await rehydrateUser();
        if (outcome !== 'auth_failed') {
          setPhase(outcome === 'transient' ? 'transient' : 'done');
          return;
        }
        // Stale persisted token — fall through to the cookie-refresh restore.
      }
      const outcome = await restoreSession();
      // Terminal 401: clear the (stale) token so AuthGuard redirects to login —
      // otherwise a dead session would render the app with a null user.
      if (outcome === 'auth_failed') useAuthStore.getState().logout();
      setPhase(outcome === 'transient' ? 'transient' : 'done');
    })();
  }, []);

  useEffect(() => {
    if (hydrated) { setPhase('done'); return; }
    if (triedRef.current) return;
    triedRef.current = true;
    run();
  }, [hydrated, run]);

  if (phase === 'restoring') return <PageSkeleton variant="manager" />;
  if (phase === 'transient') {
    return (
      <div className={styles.screen}>
        <p className={styles.message}>Couldn’t reach Markcat. Check your connection.</p>
        <button className={styles.button} onClick={run}>Retry</button>
      </div>
    );
  }
  return <>{children}</>;
}
